'use client';
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { MotionConfig } from 'motion/react';
import { detectCapability, type Capability } from '@/lib/three/capability';
import { SLIDE_COUNT, slides } from '@/lib/presentation/slides';
import { presentation, usePresentation } from '@/lib/presentation/store';
import { SlideOverlay } from './SlideOverlay';
import { SlideIndicator } from './SlideIndicator';
import { useSlideInput } from './useSlideInput';

// three.js, R3F, post-processing and every scene load only on capable devices, after `load`.
const World = dynamic(() => import('@/components/world/World'), { ssr: false, loading: () => null });

function LiveRegion() {
  const index = usePresentation((s) => s.index);
  return (
    <p className="sr-only" aria-live="polite" aria-atomic="true">
      {`Scene ${index + 1} of ${SLIDE_COUNT}: ${slides[index].label}`}
    </p>
  );
}

function Loader({ show }: { show: boolean }) {
  return (
    <div className="v2-loader" data-show={show} role="status" aria-hidden={!show}>
      <p className="text-[0.7rem] font-semibold tracking-[0.3em] text-white/80">MOVIO</p>
      <p className="mt-1 text-xs text-white/50">Building the MOVIO world…</p>
      <span className="mt-3 block h-px w-40 overflow-hidden bg-white/10">
        <span className="block h-full w-1/3 animate-[v2-loading_1.2s_ease-in-out_infinite] bg-glow motion-reduce:animate-none" />
      </span>
    </div>
  );
}

/** Under reduced motion the camera cuts between scenes; a short veil hides the cut. */
function CutVeil() {
  const index = usePresentation((s) => s.index);
  const reduced = usePresentation((s) => s.reducedMotion);
  if (!reduced) return null;
  return <div key={index} className="v2-veil" aria-hidden="true" />;
}

function ScrollHint() {
  const index = usePresentation((s) => s.index);
  return (
    <div className="v2-scroll-hint" data-show={index === 0} aria-hidden="true">
      <span>Scroll to explore</span>
      <span className="v2-scroll-line" />
    </div>
  );
}

/**
 * MOVIO V2 — cinematic 3D presentation.
 *
 *   Presentation
 *   ├── World (one persistent WebGL canvas: camera director + 14 stations)
 *   ├── SlideOverlay (eyebrow, headline, short copy, CTA per scene)
 *   ├── SlideIndicator (rail, 03 / 14, previous / next)
 *   └── useSlideInput (wheel, touch, keys, hash links)
 */
export default function Presentation() {
  const [capability, setCapability] = useState<Capability | null>(null);
  const [mountWorld, setMountWorld] = useState(false);
  const [failed, setFailed] = useState(false);
  const worldReady = usePresentation((s) => s.worldReady);
  const reducedMotion = usePresentation((s) => s.reducedMotion);
  const slideKey = usePresentation((s) => slides[s.index].key);

  useEffect(() => {
    const cap = detectCapability();
    const now = performance.now();
    presentation.set({
      reducedMotion: cap.reducedMotion,
      compact: cap.tier !== 'high',
      enteredAt: now,
      storyStartAt: now,
    });
    setCapability(cap);
    if (cap.tier === 'none') return;

    // Keep the headline (LCP), CTAs and input handling ahead of three.js parsing.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let cancelled = false;
    const mount = () => {
      // Canvas labels are drawn with DM Sans; wait for it so textures never use a fallback font.
      const fonts = document.fonts?.load('600 48px "DM Sans"').catch(() => undefined);
      Promise.race([fonts, new Promise((r) => setTimeout(r, 1500))]).then(() => {
        if (!cancelled) setMountWorld(true);
      });
    };
    const schedule = () => {
      if (w.requestIdleCallback) idleId = w.requestIdleCallback(mount, { timeout: 900 });
      else window.setTimeout(mount, 200);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('load', schedule);
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
    };
  }, []);

  useSlideInput(!!capability);

  const live = mountWorld && !!capability && !failed;

  return (
    <MotionConfig reducedMotion="user">
      <section
        id="presentation"
        className="v2-stage"
        aria-roledescription="carousel"
        aria-label="The MOVIO story in 14 scenes"
        data-scene={slideKey}
        data-world={live && worldReady ? 'ready' : 'pending'}
      >
        <div className="v2-backdrop" aria-hidden="true" />
        {live && (
          <div className="v2-world" aria-hidden="true">
            <World
              capability={capability}
              onFail={() => {
                setFailed(true);
                presentation.set({ worldReady: false });
              }}
            />
          </div>
        )}
        <div className="v2-scrim" aria-hidden="true" />
        <SlideOverlay />
        <SlideIndicator />
        <ScrollHint />
        <Loader show={live && !worldReady} />
        {live && worldReady && reducedMotion && <CutVeil />}
        <LiveRegion />
      </section>
    </MotionConfig>
  );
}
